import React, { useState } from 'react';

const CreatePropertyModal = ({ show, onHide, onCreateProperty }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    number_of_rooms: '',
    property_type: '',
    description: ''
  });
  const [images, setImages] = useState([]);
  const [defaultImageIndex, setDefaultImageIndex] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let hasError = false;

    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError(`Image "${file.name}" exceeds 10MB limit`);
        hasError = true;
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        hasError = true;
        return;
      }

      validFiles.push({
        file,
        name: file.name,
        size: file.size,
        preview: URL.createObjectURL(file),
        id: Date.now() + Math.random()
      });
    });

    if (!hasError && validFiles.length > 0) {
      setImages(prev => [...prev, ...validFiles]);
      setError('');
    }
  };

  const removeImage = (imageId) => {
    setImages(prev => {
      const filtered = prev.filter(img => img.id !== imageId);
      // Adjust default image index if needed
      if (defaultImageIndex >= filtered.length && filtered.length > 0) {
        setDefaultImageIndex(filtered.length - 1);
      } else if (filtered.length === 0) {
        setDefaultImageIndex(0);
      }
      return filtered;
    });
  };

  const setDefaultImage = (index) => {
    setDefaultImageIndex(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Prepare images array with isDefault flag for backend
      const imagesForSubmit = images.map((img, index) => ({
        file: img.file,
        isDefault: index === defaultImageIndex
      }));

      // Prepare form data for property creation
      const submitData = {
        ...formData,
        images: imagesForSubmit
      };

      const response = await onCreateProperty(submitData);

      if (response.success) {
        // Reset form and close modal
        setFormData({
          name: '',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          country: '',
          number_of_rooms: '',
          property_type: '',
          description: ''
        });
        setImages([]);
        setDefaultImageIndex(0);
        setActiveTab('basic');
        onHide();
      } else {
        setError(response.error || 'Failed to create property');
      }
    } catch (err) {
      setError('Failed to create property');
      console.error('Error creating property:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      number_of_rooms: '',
      property_type: '',
      description: ''
    });
    setImages([]);
    setDefaultImageIndex(0);
    setActiveTab('basic');
    setError('');
    onHide();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h5 className="text-xl font-semibold text-gray-900 flex items-center">
            <svg className="w-5 h-5 mr-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create New Property
          </h5>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            onClick={handleClose}
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
            <div className="flex-1 overflow-y-auto p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )}

              {/* Tab Navigation */}
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                    activeTab === 'basic' 
                      ? 'border-emerald-500 text-emerald-600 bg-emerald-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveTab('basic')}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 21l8-8-8-8" />
                  </svg>
                  Basic Information
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                    activeTab === 'address' 
                      ? 'border-emerald-500 text-emerald-600 bg-emerald-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveTab('address')}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Address Details
                </button>
                <button
                  type="button"
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                    activeTab === 'images' 
                      ? 'border-emerald-500 text-emerald-600 bg-emerald-50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  onClick={() => setActiveTab('images')}
                >
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Images ({images.length})
                </button>
              </div>

              {/* Tab Content */}
              <div>
                {/* Basic Information Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          Property Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter property name"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="md:col-span-1">
                        <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-2">
                          Property Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                          id="property_type"
                          name="property_type"
                          value={formData.property_type}
                          onChange={handleChange}
                          required
                          disabled={loading}
                        >
                          <option value="">Select property type</option>
                          <option value="multi_rental">Rooming House</option>
                          <option value="single_rental">Standard Rental</option>
                        </select>
                      </div>

                      {formData.property_type === 'multi_rental' && (
                        <div className="md:col-span-2">
                          <label htmlFor="number_of_rooms" className="block text-sm font-medium text-gray-700 mb-2">
                            Number of Rooms <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                            id="number_of_rooms"
                            name="number_of_rooms"
                            value={formData.number_of_rooms}
                            onChange={handleChange}
                            placeholder="Enter number of rooms"
                            min="1"
                            required
                            disabled={loading}
                          />
                        </div>
                      )}

                      <div className="md:col-span-4">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows="4"
                          placeholder="Enter property description (optional)"
                          disabled={loading}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Details Tab */}
                {activeTab === 'address' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                          Street Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Enter street address"
                          required
                          disabled={loading}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                        <div className="lg:col-span-3">
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Enter city"
                            required
                            disabled={loading}
                          />
                        </div>

                        <div className="lg:col-span-2">
                          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                            State/Province <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            placeholder="Enter state/province"
                            required
                            disabled={loading}
                          />
                        </div>

                        <div className="lg:col-span-1">
                          <label htmlFor="zip_code" className="block text-sm font-medium text-gray-700 mb-2">
                            ZIP/Postal Code
                          </label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                            id="zip_code"
                            name="zip_code"
                            value={formData.zip_code}
                            onChange={handleChange}
                            placeholder="Enter ZIP/postal code"
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                            Country
                          </label>
                          <input
                            type="text"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="Enter country"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Property Images
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <div className="mb-4">
                          <input
                            type="file"
                            id="property-image-upload"
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 transition-colors duration-200"
                            multiple
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={loading}
                          />
                          <p className="text-sm text-gray-500 mt-2">
                            Select multiple images. Maximum 10MB per image.
                            Supported formats: JPG, PNG, GIF, WebP
                          </p>
                        </div>

                        {images.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {images.map((image, index) => (
                              <div key={image.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                <img
                                  src={image.preview}
                                  className="w-full h-32 object-cover"
                                  alt={image.name}
                                />
                                <div className="p-3">
                                  <h6 className="text-sm font-medium text-gray-900 truncate mb-1">
                                    {image.name}
                                  </h6>
                                  <p className="text-xs text-gray-500 mb-2">
                                    {(image.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                  <div className="space-y-2">
                                    <div className="flex items-center">
                                      <input
                                        className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 focus:ring-emerald-500 focus:ring-2"
                                        type="radio"
                                        name="defaultImage"
                                        id={`default-${image.id}`}
                                        checked={defaultImageIndex === index}
                                        onChange={() => setDefaultImage(index)}
                                      />
                                      <label
                                        className="ml-2 text-xs text-gray-700"
                                        htmlFor={`default-${image.id}`}
                                      >
                                        Default Image
                                      </label>
                                    </div>
                                    <button
                                      type="button"
                                      className="text-red-600 hover:text-red-800 text-xs font-medium flex items-center transition-colors duration-200"
                                      onClick={() => removeImage(image.id)}
                                    >
                                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {images.length === 0 && (
                          <div className="text-center py-8 text-gray-500">
                            <svg className="w-16 h-16 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p>No images uploaded yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Property
                  </>
                )}
              </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePropertyModal;
